import { IKycData } from 'shared/interfaces/i-api-kyc'
import {
	FormatEnumToList,
	CountrySpecificValues,
	getMarket,
	GetAllCountriesWithTranslation,
} from 'shared/utils/helpers'
import {
	PurposeOfDeposit,
	MoneySource,
	OccupationType,
	TaxCountry,
	PropertyType,
} from 'shared/constants/kycTypes'
import { useTranslation } from 'react-i18next'
import { TranslationFile } from 'i18n'
import i18next from 'i18next'
import appConfig from 'app.config'

export const DepositInformationFormFi = (data: IKycData) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Dropdown,
	])
	return {
		fieldSetName: t('forms:DepositInfoHeader'),
		formName: data.customerType,
		description: t('forms:DepositInfoDescription'),
		show: false,
		visible: true,
		col: 12,
		controls: [
			{
				options: FormatEnumToList(
					PurposeOfDeposit,
					PropertyType.PurposeOfDeposit,
					TranslationFile.Dropdown,
				),
				required: true,
				controlName: 'purposeOfDeposits',
				type: 'dropdown',
				label: t('forms:PurposeOfDeposits'),
				col: 12,
				value: data.deposits[0].purposeOfDeposits,
				visible: true,
			},
			{
				required: true,
				controlName: 'purposeOfDepositsOther',
				type: 'text',
				label: t('forms:PurposeOfDepositsOther'),
				col: 12,
				value: data.deposits[0].purposeOfDepositsOther,
				visible: false,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'purposeOfDeposits',
								rule: 'equal',
								value: 'Other', //'Other'
							},
						],
					},
				],
			},
			{
				options: FormatEnumToList(
					MoneySource,
					PropertyType.MoneySource,
					TranslationFile.Dropdown,
				),
				required: true,
				controlName: 'moneySource',
				type: 'dropdown',
				label: t('forms:AssetsFrom'),
				col: 12,
				value: data.deposits[0].moneySource,
				visible: true,
				fullSize: true,
			},
			{
				required: true,
				controlName: 'moneySourceOther',
				type: 'text',
				label: t('forms:AssetsFromOther'),
				col: 12,
				value: data.deposits[0].moneySourceOther,
				visible: false,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'moneySource',
								rule: 'equal',
								value: 'Other', //'Other'
							},
						],
					},
				],
			},
			{
				// This is due to duplicated translation strings with different
				// content regarding choosen language
				options: [
					{
						value: 'Yes',
						label: t('dropdown:YesDepositOwner'),
					},
					{
						value: 'YesGuardian',
						label: t('dropdown:YesGuardian'),
					},
					{
						value: 'Children',
						label: t('dropdown:Children'),
					},
					{
						value: 'Other',
						label: t('dropdown:OtherDepositOwner'),
					},
				],
				required: true,
				controlName: 'depositOwner',
				type: 'dropdown',
				label: t('forms:DepositOwner'),
				col: 12,
				value: data.deposits[0].depositOwner,
				visible: true,
				fullSize: true,
			},
			{
				options: CountrySpecificValues(
					TranslationFile.Dropdown,
					PropertyType.ExpectedDeposits,
				),
				required: true,
				controlName: 'expectedDeposits',
				type: 'dropdown',
				label: t('forms:ExpectedDeposits'),
				col: 12,
				value: data.deposits[0].expectedDeposits,
				visible: true,
				fullSize: false,
			},
			{
				options: CountrySpecificValues(
					TranslationFile.Dropdown,
					PropertyType.ExpectedDepositsAmount,
				),
				required: true,
				controlName: 'expectedDepositsAmount',
				type: 'dropdown',
				label: t('forms:ExpectedDepositsAmount'),
				col: 12,
				value: data.deposits[0].expectedDepositsAmount,
				visible: true,
				fullSize: false,
			},
			{
				options: CountrySpecificValues(
					TranslationFile.Dropdown,
					PropertyType.ExpectedWithdrawls,
				),
				required: true,
				controlName: 'expectedWithdrawals',
				type: 'dropdown',
				label: t('forms:ExpectedWithdrawls'),
				col: 12,
				value: data.deposits[0].expectedWithdrawals,
				visible: true,
				fullSize: false,
			},
			{
				options: CountrySpecificValues(
					TranslationFile.Dropdown,
					PropertyType.ExpectedWithdrawlsAmount,
				),
				required: true,
				controlName: 'expectedWithdrawalsAmount',
				type: 'dropdown',
				label: t('forms:ExpectedWithdrawlsAmount'),
				col: 12,
				value: data.deposits[0].expectedWithdrawalsAmount,
				visible: true,
				fullSize: false,
			},
			{
				options: FormatEnumToList(
					OccupationType,
					PropertyType.Occupation,
					TranslationFile.Dropdown,
				),
				required: true,
				controlName: 'occupationType',
				type: 'dropdown',
				label: t('forms:Occupation'),
				col: 12,
				value: data.employments[0].occupationType,
				visible: true,
			},
			{
				options: CountrySpecificValues(
					TranslationFile.Dropdown,
					PropertyType.Income,
				),
				required: true,
				controlName: 'income',
				type: 'dropdown',
				label: t('forms:AnnualIncome'),
				col: 12,
				value: data.employments[0].income,
				visible: true,
			},
			{
				options: FormatEnumToList(
					TaxCountry,
					PropertyType.TaxCountry,
					TranslationFile.Dropdown,
				),
				required: true,
				controlName: 'taxCountry',
				type: 'dropdown',
				label:
					i18next.language === 'en'
						? t('forms:ResidenceTaxPurposes', {
								country: getMarket(),
						  })
						: t('forms:ResidenceTaxPurposes'),
				col: 12,
				value: data.taxCountry,
				visible: true,
			},
			{
				options: GetAllCountriesWithTranslation(appConfig.country),
				required: true,
				controlName: 'taxCountryOther',
				type: 'dropdown',
				label: t('forms:ResidenceTaxPurposesOther'),
				value: data.taxCountryOther,
				visible: false,
				col: 12,

				condition: [
					{
						when: 'all',
						action: 'hide',
						items: [
							{
								field: 'taxCountry',
								rule: 'equal',
								value: 'Yes', //'Yes'
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'tin',
				type: 'text',
				label: t('forms:Tin'),
				value: data.tin,
				col: 12,
				visible: false,
				condition: [
					{
						when: 'all',
						action: 'hide',
						items: [
							{
								field: 'taxCountry',
								rule: 'equal',
								value: 'Yes', //'Yes'
							},
						],
					},
				],
			},
			{
				options: [
					{
						value: 'false',
						label: t('No'),
					},
					{
						value: 'true',
						label: t('Yes'),
					},
				],
				required: true,
				controlName: 'fatca',
				type: 'dropdown',
				label: t('forms:Fatca'),
				value: data.fatca,
				col: 12,
				visible: true,
				tooltip: t('forms:FatcaTooltipText'),
			},
		],
	}
}
